import ActionTextIcon from "@/components/icons/ActionTextIcon";
import { Button, Collapse, Empty, Modal, Tabs, Tooltip } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetProductBusinessCoefficients } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { ProductCommissionsSettingsBusinessCoefficients } from "../types";
import { getCommissionKindTabLabel, getProductCommissionsSettingsLabel } from "../utils";
import { BusinessCoefficientsDescriptions } from "./BusinessCoefficientsDescriptions";

type Props = {
  productId: UUID;
};

export const ProductBusinessCoefficients = ({ productId }: Props) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<ProductCommissionsSettingsBusinessCoefficients[]>([]);

  const getProductBusinessCoefficients = useGetProductBusinessCoefficients();

  const handleOpen = () => {
    setIsOpen(true);
    getProductBusinessCoefficients.mutate({ id: productId }, { onSuccess: (data) => setData(data) });
  };

  const commissionKinds = [...new Set(data.map((settings) => settings.commissionKind))];

  return (
    <>
      <Tooltip title={t("commissions.productCommissionsSettings.actions.showBusinessCoefficients")}>
        <span>
          <ActionTextIcon icon="fund-view" color="blue" onClick={handleOpen} />
        </span>
      </Tooltip>

      <Modal
        width={ModalSizes.MEDIUM}
        open={isOpen}
        loading={getProductBusinessCoefficients.isPending}
        title={t("commissions.productCommissionsSettings.titles.businessCoefficients")}
        destroyOnHidden
        onCancel={() => setIsOpen(false)}
        footer={<Button onClick={() => setIsOpen(false)}>{t("common.close")}</Button>}
      >
        {commissionKinds.length ? (
          <Tabs
            items={commissionKinds.map((commissionKind) => {
              const commissionKindSettings = data.filter((settings) => settings.commissionKind === commissionKind);

              return {
                key: commissionKind,
                label: getCommissionKindTabLabel(commissionKind, t),
                children: (
                  <Collapse
                    defaultActiveKey={commissionKindSettings[0]?.id}
                    items={commissionKindSettings.map((settings) => ({
                      key: settings.id,
                      label: getProductCommissionsSettingsLabel(settings, t),
                      children: (
                        <BusinessCoefficientsDescriptions businessCoefficients={settings.businessCoefficients} />
                      ),
                    }))}
                  />
                ),
              };
            })}
          />
        ) : (
          <Empty description={t("commissions.productCommissionsSettings.helpers.noBusinessCoefficients")} />
        )}
      </Modal>
    </>
  );
};
